import { db } from "../firebase/firebaseConfig";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  setDoc,
  increment,
  limit,
} from "firebase/firestore";

/**
 * Fetch all cancellations from the cancellation_track collection
 */
export const getAllCancellations = async () => {
  try {
    const cancellationsRef = collection(db, "cancellation_track");
    const querySnapshot = await getDocs(cancellationsRef);

    const cancellations = [];
    querySnapshot.forEach((doc) => {
      cancellations.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return cancellations;
  } catch (error) {
    console.error("Error fetching all cancellations:", error);
    throw error;
  }
};

/**
 * Fetch cancellations for a specific service provider
 */
export const getProviderCancellations = async (providerId) => {
  try {
    const cancellationsRef = collection(db, "cancellation_track");
    const q = query(
      cancellationsRef,
      where("service_provider_id", "==", providerId)
    );
    const querySnapshot = await getDocs(q);

    const cancellations = [];
    querySnapshot.forEach((doc) => {
      cancellations.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return cancellations;
  } catch (error) {
    console.error("Error fetching provider cancellations:", error);
    throw error;
  }
};

/**
 * Apply fine to a cancellation and update records based on who cancelled
 * SUPPORTS BOTH: Service Provider cancellations & App User cancellations
 */
export const applyCancellationFine = async (cancellationId, fineAmount) => {
  try {
    const cancellationRef = doc(db, "cancellation_track", cancellationId);
    const cancellationDoc = await getDoc(cancellationRef);

    if (!cancellationDoc.exists()) {
      throw new Error("Cancellation not found");
    }

    const cancellationData = cancellationDoc.data();
    
    // ✅ NEW: Check who cancelled the service
    const cancelledBy = cancellationData.cancelled_by || "provider"; // Default to provider for backward compatibility
    
    if (cancelledBy === "app_user") {
      // Apply fine to APP USER
      return await applyFineToAppUser(cancellationId, cancellationData, fineAmount);
    } else {
      // Apply fine to SERVICE PROVIDER (existing logic)
      return await applyFineToServiceProvider(cancellationId, cancellationData, fineAmount);
    }
  } catch (error) {
    console.error("Error applying cancellation fine:", error);
    throw error;
  }
};

export const recordProviderDelayIncident = async (orderData) => {
  try {
    const providerId = orderData?.service_providers_id?.id || orderData?.service_providers_id;

    if (!providerId) {
      throw new Error("Service provider ID not found in booking");
    }

    const orderId = orderData?.id || orderData?.order_id;
    if (!orderId) {
      throw new Error("Order ID not found in booking");
    }

    const currentStatus = String(orderData?.booking_status || "").toLowerCase();
    const isDelayLocked = Boolean(orderData?.delay_locked);
    const blockedStatuses = new Set([
      "completed",
      "progress",
      "cancelled_by_admin",
      "cancelled_by_provider",
      "cancelled_by_user",
    ]);

    if (blockedStatuses.has(currentStatus) && !isDelayLocked) {
      throw new Error("لا يمكن تسجيل مخالفة تأخير على هذا الطلب في حالته الحالية.");
    }

    const providerName =
      orderData?.service_providers_id?.salon_name ||
      orderData?.service_providers_id?.full_name ||
      orderData?.service_provider_name ||
      null;
    const providerEmail =
      orderData?.service_providers_id?.email ||
      orderData?.service_provider_email ||
      null;
    const providerPhone =
      orderData?.service_providers_id?.mobile ||
      orderData?.service_provider_phone ||
      null;
    const providerCompany =
      orderData?.service_providers_id?.salon_name ||
      orderData?.service_provider_company ||
      null;
    const appUserId = orderData?.app_user_id?.id || orderData?.app_user_id || null;
    const serviceName =
      orderData?.serviceProviderServices?.name ||
      orderData?.service_name ||
      null;
    const bookingAmount = Number(
      orderData?.total_after_discount ||
        orderData?.total ||
        orderData?.amount ||
        orderData?.booking_amount ||
        0
    );
    const nowIso = new Date().toISOString();

    const providerIncidentsSnapshot = await getDocs(
      query(collection(db, "cancellation_track"), where("service_provider_id", "==", providerId))
    );

    const previousDelayIncidents = providerIncidentsSnapshot.docs.filter((docSnap) => {
      const data = docSnap.data();
      return String(data.cancellation_type || "").toLowerCase() === "provider_delay";
    });

    const alreadyRecordedForOrder = previousDelayIncidents.some((docSnap) => {
      const data = docSnap.data();
      return String(data.order_id || "") === String(orderId);
    });

    if (alreadyRecordedForOrder) {
      throw new Error("تم تسجيل مخالفة تأخير على هذا الطلب مسبقاً ولا يمكن تكرارها.");
    }

    const occurrenceNumber = previousDelayIncidents.length + 1;
    const isWarningOnly = occurrenceNumber === 1;
    const fineAmount = isWarningOnly ? 0 : 30;
    const reasonText = isWarningOnly
      ? `إنذار تأخير أول لمقدم الخدمة - الطلب #${orderId}`
      : `غرامة تأخير مقدم الخدمة - الطلب #${orderId}`;

    const incidentRef = await addDoc(collection(db, "cancellation_track"), {
      service_provider_id: providerId,
      service_provider_name: providerName,
      service_provider_email: providerEmail,
      service_provider_phone: providerPhone,
      service_provider_company: providerCompany,
      app_user_id: appUserId,
      order_id: orderId || null,
      service_name: serviceName,
      booking_amount: bookingAmount,
      cancellation_type: "provider_delay",
      incident_type: "provider_delay",
      policy_type: "delay_policy",
      incident_sequence: occurrenceNumber,
      cancelled_by: "provider",
      status: isWarningOnly ? "warning_issued" : "confirmed",
      deduction_status: isWarningOnly ? "warning_only" : "pending_settlement",
      total_fine: fineAmount,
      admin_fine_amount: fineAmount,
      base_fine: fineAmount,
      percentage_fee: 0,
      fixed_fee: 0,
      fine_applied_by_admin: !isWarningOnly,
      warning_issued: isWarningOnly,
      is_warning_only: isWarningOnly,
      fine_charged_to: "provider",
      cancellation_reason: reasonText,
      cancellation_date: nowIso,
      created_at: nowIso,
      updated_at: nowIso,
      booking_status_at_report: orderData?.booking_status || null,
    });

    await updateDoc(doc(db, "Booking", String(orderId)), {
      delay_locked: true,
      delay_locked_at: orderData?.delay_locked_at || nowIso,
      updatedAt: nowIso,
    });

    const providerRef = doc(db, "service_providers", providerId);
    const providerSnap = await getDoc(providerRef);

    if (providerSnap.exists()) {
      const providerData = providerSnap.data();
      await updateDoc(providerRef, {
        delay_incident_count: Number(providerData.delay_incident_count || 0) + 1,
        delay_warning_count: Number(providerData.delay_warning_count || 0) + (isWarningOnly ? 1 : 0),
        total_delay_fines: Number(providerData.total_delay_fines || 0) + fineAmount,
        pending_delay_fines: Number(providerData.pending_delay_fines || 0) + fineAmount,
        last_delay_date: nowIso,
      });
    }

    let pendingFineId = null;

    if (!isWarningOnly) {
      const pendingFineRef = await addDoc(collection(db, "pending_fine_deductions"), {
        service_provider_id: providerId,
        service_provider_name: providerName,
        cancellation_id: incidentRef.id,
        order_id: orderId || null,
        source_booking_id: orderId || null,
        source_invoice_id: null,
        original_fine_amount: fineAmount,
        pending_amount: fineAmount,
        applied_amount: 0,
        reason: reasonText,
        status: "pending",
        charged_to: "provider",
        source_type: "provider_delay",
        penalty_type: "provider_delay",
        created_at: nowIso,
        updated_at: nowIso,
        applied_history: [],
      });

      pendingFineId = pendingFineRef.id;

      await updateDoc(doc(db, "cancellation_track", incidentRef.id), {
        provider_pending_fine_id: pendingFineId,
        updated_at: nowIso,
      });
    }

    return {
      success: true,
      incidentId: incidentRef.id,
      pendingFineId,
      occurrenceNumber,
      isWarningOnly,
      fineAmount,
      message: isWarningOnly
        ? "تم تسجيل أول حالة تأخير كمخالفة تحذيرية بدون غرامة."
        : "تم تسجيل تأخير لمقدم الخدمة وإضافة غرامة 30 ر.س إلى خصومات التسوية القادمة.",
    };
  } catch (error) {
    console.error("Error recording provider delay incident:", error);
    throw error;
  }
};

/**
 * Apply fine specifically to SERVICE PROVIDER
 */
async function applyFineToServiceProvider(cancellationId, cancellationData, fineAmount) {
  try {
    const providerId = cancellationData.service_provider_id;

    if (!providerId) {
      throw new Error("Service provider ID not found in cancellation");
    }
    const nowIso = new Date().toISOString();
    const remainingFineAmount = Number(fineAmount || 0);
    const totalDeductedFromInvoices = 0;

    // STEP 2: Update provider's fine records
    const providerRef = doc(db, "service_providers", providerId);
    const providerDoc = await getDoc(providerRef);

    if (providerDoc.exists()) {
      const providerData = providerDoc.data();
      const currentFines = providerData.total_cancellation_fines || 0;
      const cancellationCount = providerData.order_cancellation_count || 0;

      await updateDoc(providerRef, {
        total_cancellation_fines: currentFines + remainingFineAmount,
        pending_cancellation_fines: (providerData.pending_cancellation_fines || 0) + remainingFineAmount,
        order_cancellation_count: cancellationCount + 1,
        last_fine_date: nowIso,
      });
    }

    // Create pending fine record. It will be applied later on next eligible settlement invoice.
    const pendingFineRef = await addDoc(collection(db, "pending_fine_deductions"), {
      service_provider_id: providerId,
      service_provider_name: cancellationData.service_provider_name || null,
      cancellation_id: cancellationId,
      order_id: cancellationData.order_id || null,
      source_booking_id: cancellationData.order_id || null,
      source_invoice_id: null,
      original_fine_amount: remainingFineAmount,
      pending_amount: remainingFineAmount,
      applied_amount: 0,
      reason: `إلغاء من مقدم الخدمة - الطلب #${cancellationData.order_id}`,
      status: "pending",
      charged_to: "provider",
      created_at: nowIso,
      updated_at: nowIso,
      applied_history: [],
    });

    // Mark cancelled booking invoice as refund candidate for ERP credit-note generation.
    const invoiceCandidates = await getDocs(
      query(
        collection(db, "service_provider_sale_invoices"),
        where("serviceProviders", "==", providerId),
        where("booking", "==", cancellationData.order_id),
        limit(1)
      )
    );

    if (!invoiceCandidates.empty) {
      const invoiceDoc = invoiceCandidates.docs[0];
      const invoiceData = invoiceDoc.data();
      const grossAmount =
        invoiceData.total_after_discount_before_fine ||
        invoiceData.total_after_discount ||
        invoiceData.amount ||
        0;

      await updateDoc(doc(db, "service_provider_sale_invoices", invoiceDoc.id), {
        needs_refund: true,
        refund_status: "pending",
        refund_initiated_at: nowIso,
        refund_reason: "Provider cancelled order",
        cancelled_order_id: cancellationData.order_id || null,
        cancelled_by: "provider",
        fine_charged_to: "provider",
        provider_pending_fine_amount: remainingFineAmount,
        provider_pending_fine_id: pendingFineRef.id,
        fine_deducted: false,
        fine_deductions_total: invoiceData.fine_deductions_total || 0,
        gross_amount: grossAmount,
        original_service_amount: grossAmount,
        updated_at: nowIso,
      });

      await updateDoc(doc(db, "pending_fine_deductions", pendingFineRef.id), {
        source_invoice_id: invoiceDoc.id,
        updated_at: nowIso,
      });
    }

    // STEP 5: Update cancellation document status
    await updateDoc(doc(db, "cancellation_track", cancellationId), {
      deduction_status: "pending_settlement",
      deducted_at: null,
      fine_applied_by_admin: true,
      admin_fine_amount: fineAmount,
      deduction_reason: `إلغاء من مقدم الخدمة - الطلب #${cancellationData.order_id}`,
      deducted_from_invoices: [],
      total_deducted: totalDeductedFromInvoices,
      remaining_fine_amount: remainingFineAmount,
      provider_pending_fine_id: pendingFineRef.id,
      updated_at: nowIso,
    });

    const message = `تم تسجيل غرامة مقدم الخدمة بقيمة ${remainingFineAmount.toFixed(2)} ر.س كغرامة معلقة للتسويات القادمة، وتم تعليم فاتورة الحجز الملغي كمرشحة لاسترجاع كامل للعميل.`;

    return {
      success: true,
      message,
      chargedTo: "provider",
      deductionDetails: {
        total_fine: fineAmount,
        deducted_from_invoices: 0,
        pending_amount: remainingFineAmount,
        updated_invoices: [],
        pending_fine_id: pendingFineRef.id,
      },
    };
  } catch (error) {
    console.error("Error applying fine to service provider:", error);
    throw error;
  }
}

export const applyPendingProviderFineToNextSettlement = async (pendingFineId) => {
  try {
    const pendingRef = doc(db, "pending_fine_deductions", pendingFineId);
    const pendingSnap = await getDoc(pendingRef);

    if (!pendingSnap.exists()) {
      throw new Error("Pending fine record not found");
    }

    const pendingData = pendingSnap.data();
    const providerId = pendingData.service_provider_id;
    const pendingAmount = Number(pendingData.pending_amount || 0);
    const pendingStatus = String(pendingData.status || "").toLowerCase();
    const sourceType = String(pendingData.source_type || "provider_cancellation").toLowerCase();
    const isDelayPenalty = sourceType === "provider_delay";
    const fineReason =
      pendingData.reason ||
      (isDelayPenalty ? "غرامة تأخير مقدم الخدمة" : "غرامة إلغاء مقدم الخدمة");
    const deductionType = isDelayPenalty
      ? "provider_delay_fine"
      : "provider_pending_cancellation_fine";

    if (pendingAmount <= 0) {
      const nowIso = new Date().toISOString();
      if (pendingStatus !== "deducted") {
        await updateDoc(pendingRef, {
          pending_amount: 0,
          status: "deducted",
          updated_at: nowIso,
        });
      }

      if (pendingData.cancellation_id) {
        await updateDoc(doc(db, "cancellation_track", pendingData.cancellation_id), {
          deduction_status: "deducted",
          deducted_at: nowIso,
          last_pending_fine_id: pendingFineId,
          updated_at: nowIso,
        });
      }

      throw new Error("هذه الغرامة تم خصمها بالكامل مسبقاً ولا يمكن إعادة تطبيقها.");
    }

    if (pendingStatus === "deducted") {
      throw new Error("هذه الغرامة تم خصمها بالكامل مسبقاً ولا يمكن إعادة تطبيقها.");
    }

    if (!providerId) {
      throw new Error("No provider linked to pending fine");
    }

    const appliedHistory = Array.isArray(pendingData.applied_history) ? pendingData.applied_history : [];
    const alreadyAppliedInvoiceIds = new Set(
      [
        ...appliedHistory.map((entry) => entry?.invoice_id).filter(Boolean),
        pendingData.last_applied_invoice_id,
      ]
        .filter(Boolean)
        .map((id) => String(id))
    );

    const invoicesSnapshot = await getDocs(
      query(
        collection(db, "service_provider_sale_invoices"),
        where("serviceProviders", "==", providerId),
        where("payment_status", "==", "pending")
      )
    );

    const eligibleInvoices = invoicesSnapshot.docs.filter((invoiceDoc) => {
      const inv = invoiceDoc.data();
      const refundStatus = String(inv.refund_status || "").toLowerCase();
      const isRefundStatusMarked = refundStatus !== "" && !["none", "not_required", "no_refund"].includes(refundStatus);
      const isRefund = inv.is_refund === true || inv.needs_refund === true || isRefundStatusMarked;
      const isReturn = inv.is_return === true;
      const isCreditNote = inv.doctype === "Credit Note" || inv.is_credit_note === true;
      const isSourceBooking =
        pendingData.source_booking_id && String(inv.booking || "") === String(pendingData.source_booking_id);
      const isAlreadyAppliedForThisFine = alreadyAppliedInvoiceIds.has(String(invoiceDoc.id));

      return !isRefund && !isReturn && !isCreditNote && !isSourceBooking && !isAlreadyAppliedForThisFine;
    });

    if (eligibleInvoices.length === 0) {
      throw new Error("تم تطبيق هذه الغرامة على كل فواتير التسوية المتاحة حالياً. انتظر تسوية جديدة قبل إعادة المحاولة.");
    }

    const targetInvoiceDoc = eligibleInvoices[0];
    const invoiceData = targetInvoiceDoc.data();

    const saleAmount =
      Number(invoiceData.total_after_discount_before_fine || 0) ||
      Number(invoiceData.total_after_discount || 0) ||
      Number(invoiceData.amount || 0);
    const currentNetAfterFines =
      invoiceData.net_after_fines !== undefined
        ? Number(invoiceData.net_after_fines || 0)
        : saleAmount;

    // Safety cap: never deduct more than the settlement/invoice net amount.
    const deductionAmount = Math.min(pendingAmount, Math.max(currentNetAfterFines, 0));

    if (deductionAmount <= 0) {
      throw new Error("قيمة التسوية الحالية لا تسمح بخصم الغرامة.");
    }

    const nowIso = new Date().toISOString();
    const newNetAfterFines = Math.max(currentNetAfterFines - deductionAmount, 0);
    const currentDeductions = Number(invoiceData.total_deductions || 0);
    const currentFineDeductions = Number(invoiceData.fine_deductions_total || 0);
    const existingInvoiceDeductions = Array.isArray(invoiceData.deductions) ? invoiceData.deductions : [];

    const invoiceUpdate = {
      total_after_discount_before_fine: saleAmount,
      total_after_discount: saleAmount,
      amount: saleAmount,
      net_after_fines: newNetAfterFines,
      total_deductions: currentDeductions + deductionAmount,
      fine_deductions_total: currentFineDeductions + deductionAmount,
      fine_deducted: true,
      fine_status: "deducted",
      fine_last_reason: fineReason,
      fine_charged_to: "provider",
      deductions: [
        ...existingInvoiceDeductions,
        {
          type: deductionType,
          pending_fine_id: pendingFineId,
          cancellation_id: pendingData.cancellation_id || null,
          amount: deductionAmount,
          reason: fineReason,
          deducted_at: nowIso,
          charged_to: "provider",
        },
      ],
      last_deducted_at: nowIso,
      updated_at: nowIso,
    };

    if (isDelayPenalty) {
      invoiceUpdate.provider_delay_deduction = true;
      invoiceUpdate.latest_penalty_id = pendingFineId;
      invoiceUpdate.latest_delay_record_id = pendingData.cancellation_id || null;
    } else {
      invoiceUpdate.latest_cancellation_id = pendingData.cancellation_id || null;
      invoiceUpdate.cancelled_by = "provider";
    }

    await updateDoc(doc(db, "service_provider_sale_invoices", targetInvoiceDoc.id), invoiceUpdate);

    const newPendingAmount = Math.max(pendingAmount - deductionAmount, 0);

    await updateDoc(pendingRef, {
      pending_amount: newPendingAmount,
      applied_amount: Number(pendingData.applied_amount || 0) + deductionAmount,
      status: newPendingAmount > 0 ? "partial" : "deducted",
      last_applied_at: nowIso,
      last_applied_invoice_id: targetInvoiceDoc.id,
      applied_history: [
        ...appliedHistory,
        {
          invoice_id: targetInvoiceDoc.id,
          amount: deductionAmount,
          applied_at: nowIso,
        },
      ],
      updated_at: nowIso,
    });

    if (pendingData.cancellation_id) {
      await updateDoc(doc(db, "cancellation_track", pendingData.cancellation_id), {
        deduction_status: newPendingAmount > 0 ? "pending_settlement" : "deducted",
        deducted_at: newPendingAmount > 0 ? null : nowIso,
        last_pending_fine_id: pendingFineId,
        updated_at: nowIso,
      });
    }

    const providerRef = doc(db, "service_providers", providerId);
    const providerSnap = await getDoc(providerRef);

    if (providerSnap.exists()) {
      const providerData = providerSnap.data();
      const providerUpdate = {
        last_fine_deduction_at: nowIso,
      };

      if (isDelayPenalty) {
        providerUpdate.pending_delay_fines = Math.max(Number(providerData.pending_delay_fines || 0) - deductionAmount, 0);
      } else {
        providerUpdate.pending_cancellation_fines = Math.max(Number(providerData.pending_cancellation_fines || 0) - deductionAmount, 0);
      }

      await updateDoc(providerRef, providerUpdate);
    }

    return {
      success: true,
      pendingFineId,
      invoiceId: targetInvoiceDoc.id,
      deductedAmount: deductionAmount,
      remainingPendingAmount: newPendingAmount,
    };
  } catch (error) {
    console.error("Error applying pending provider fine:", error);
    throw error;
  }
};

/**
 * Apply fine specifically to APP USER
 * Deducts from the SERVICE PROVIDER's pending invoice (reduces refund to app_user)
 */
async function applyFineToAppUser(cancellationId, cancellationData, fineAmount) {
  try {
    const appUserId = cancellationData.app_user_id;
    const providerId = cancellationData.service_provider_id;
    const orderId = cancellationData.order_id;

    if (!appUserId) {
      throw new Error("App user ID not found in cancellation");
    }

    if (!providerId) {
      throw new Error("Service provider ID not found in cancellation");
    }

    if (!orderId) {
      throw new Error("Order ID not found in cancellation");
    }

    const resolveBookingId = (value) => {
      if (!value) return null;
      if (typeof value === "string") return value;
      return value.id || value.booking_id || value.uid || value.$id || null;
    };

    // ✅ STEP 1: Find THIS app_user's pending invoices from THIS provider,
    // then strictly select the invoice that belongs to the cancelled booking only.
    const invoicesSnapshot = await getDocs(
      query(
        collection(db, "service_provider_sale_invoices"),
        where("serviceProviders", "==", providerId),
        where("app_user_id", "==", appUserId),
        where("payment_status", "==", "pending")
      )
    );

    const bookingInvoices = invoicesSnapshot.docs.filter((invoiceDoc) => {
      const invoiceData = invoiceDoc.data();
      const invoiceBookingId = resolveBookingId(invoiceData.booking);
      const isRefund = invoiceData.is_refund === true;
      const isReturn = invoiceData.is_return === true;
      const isCreditNote = invoiceData.doctype === "Credit Note" || invoiceData.is_credit_note === true;
      return (
        String(invoiceBookingId || "") === String(orderId) &&
        !isRefund &&
        !isReturn &&
        !isCreditNote
      );
    });

    if (bookingInvoices.length === 0) {
      throw new Error(
        `لا يمكن خصم غرامة العميل لأن فاتورة الحجز الملغي غير متاحة أو غير صالحة للخصم. الخصم يجب أن يتم من نفس فاتورة الحجز فقط.`
      );
    }

    let remainingFineAmount = fineAmount;
    const updatedInvoices = [];

    // ✅ Deduct fine from the same cancelled booking invoice only.
    if (bookingInvoices.length > 0) {
      const invoiceDoc = bookingInvoices[0];
      const invoiceData = invoiceDoc.data();
      
      const saleAmount =
        invoiceData.total_after_discount_before_fine ||
        invoiceData.total_after_discount ||
        invoiceData.amount ||
        0;
      const currentNetAfterFines =
        invoiceData.net_after_fines !== undefined
          ? invoiceData.net_after_fines
          : saleAmount;
      const currentDeductions = invoiceData.total_deductions || 0;
      const currentFineDeductions = invoiceData.fine_deductions_total || 0;
      const lastReason = `إلغاء من العميل - الطلب #${cancellationData.order_id} (غرامة خصمت من المستحقات)`;
      
      const deductionAmount = Math.min(remainingFineAmount, currentNetAfterFines);

      if (deductionAmount > 0) {
        const invoiceRef = doc(db, "service_provider_sale_invoices", invoiceDoc.id);
        const newNetAfterFines = Math.max(currentNetAfterFines - deductionAmount, 0);
        const deductionsList = invoiceData.deductions || [];

        // ✅ Update provider's invoice with fine deduction
        // This reduces the amount provider gets and thus reduces the refund to app_user
        await updateDoc(invoiceRef, {
          total_after_discount_before_fine: saleAmount,
          total_after_discount: saleAmount,
          amount: saleAmount,
          net_after_fines: newNetAfterFines,
          total_deductions: currentDeductions + deductionAmount,
          fine_deductions_total: currentFineDeductions + deductionAmount,
          fine_deducted: true,
          fine_status: "deducted",
          fine_last_reason: lastReason,
          latest_cancellation_id: cancellationId,
          cancelled_by: "app_user", // ✅ NEW: Track who was charged
          fine_charged_to: "app_user",
          app_user_fine_deduction: true, // ✅ Mark that this deduction is for app_user fine
          deductions: [
            ...deductionsList,
            {
              cancellation_id: cancellationId,
              deduction_amount: deductionAmount,
              reason: `إلغاء من العميل - الطلب #${cancellationData.order_id}`,
              type: "app_user_cancellation_fine",
              deducted_from: "provider_pending_invoice",
              deducted_for: "app_user",
              deducted_at: new Date().toISOString(),
              charged_to: "app_user",
            },
          ],
          last_deducted_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

        updatedInvoices.push({
          invoice_id: invoiceDoc.id,
          deduction_amount: deductionAmount,
          sale_amount: saleAmount,
          net_after_fines: newNetAfterFines,
          reason: lastReason,
          charged_to: "app_user",
          deducted_from: "same_cancelled_booking_invoice",
        });

        remainingFineAmount -= deductionAmount;
      }
    }

    const totalDeductedFromInvoices = fineAmount - remainingFineAmount;

    // ✅ STEP 2: Update app_user's fine records
    const appUserRef = doc(db, "app_users", appUserId);
    const appUserDoc = await getDoc(appUserRef);

    if (appUserDoc.exists()) {
      const appUserData = appUserDoc.data();
      const currentFines = appUserData.total_cancellation_fines || 0;
      const cancellationCount = appUserData.cancellation_count || 0;

      await updateDoc(appUserRef, {
        total_cancellation_fines: currentFines + totalDeductedFromInvoices,
        pending_cancellation_fines: (appUserData.pending_cancellation_fines || 0) + remainingFineAmount,
        cancellation_count: cancellationCount + 1,
        last_fine_date: new Date().toISOString(),
      });
    }

    // Track pending deduction if there's remaining fine
    if (remainingFineAmount > 0) {
      const pendingDeductionsRef = collection(db, "pending_fine_deductions");
      await addDoc(pendingDeductionsRef, {
        app_user_id: appUserId,
        service_provider_id: providerId,
        cancellation_id: cancellationId,
        order_id: cancellationData.order_id,
        pending_amount: remainingFineAmount,
        reason: `إلغاء من العميل - الطلب #${cancellationData.order_id}`,
        status: "pending",
        charged_to: "app_user",
        note: "Remaining amount pending. It must be reconciled against the same cancelled booking invoice only.",
        created_at: new Date().toISOString(),
      });
    }

    // ✅ STEP 3: Add deducted amount to platform revenue
    if (totalDeductedFromInvoices > 0) {
      const platformRevenueRef = doc(db, "platform_revenue", "total");
      const platformRevenueSnap = await getDoc(platformRevenueRef);
      const currentRevenue = platformRevenueSnap.exists()
        ? platformRevenueSnap.data().amount || 0
        : 0;

      await setDoc(
        platformRevenueRef,
        {
          amount: increment(totalDeductedFromInvoices),
          last_updated: new Date().toISOString(),
          last_fine_added: {
            amount: totalDeductedFromInvoices,
            cancellation_id: cancellationId,
            order_id: cancellationData.order_id,
            app_user_id: appUserId,
            service_provider_id: providerId,
            type: "app_user_cancellation_fine",
            charged_to: "app_user",
            added_at: new Date().toISOString(),
          },
          previous_amount_snapshot: currentRevenue,
        },
        { merge: true }
      );
    }

    // ✅ STEP 4: Update cancellation document status
    await updateDoc(doc(db, "cancellation_track", cancellationId), {
      deduction_status: remainingFineAmount > 0 ? "pending" : "deducted",
      deducted_at: new Date().toISOString(),
      fine_applied_by_admin: true,
      admin_fine_amount: fineAmount,
      deduction_reason: `إلغاء من العميل - الطلب #${cancellationData.order_id}`,
      deducted_from_invoices: updatedInvoices,
      total_deducted: totalDeductedFromInvoices,
      remaining_fine_amount: remainingFineAmount,
      deduction_note: "Fine deducted from the same cancelled booking invoice only.",
      updated_at: new Date().toISOString(),
    });

    const message = remainingFineAmount > 0
      ? `تم تطبيق الغرامة على نفس فاتورة الحجز الملغي. خصم ${totalDeductedFromInvoices.toFixed(2)} ر.س والمتبقي ${remainingFineAmount.toFixed(2)} ر.س تم تسجيله كمبلغ معلق.`
      : `تم تطبيق الغرامة على نفس فاتورة الحجز الملغي بنجاح. خصم ${totalDeductedFromInvoices.toFixed(2)} ر.س.`;

    return {
      success: true,
      message,
      chargedTo: "app_user",
      deductionDetails: {
        total_fine: fineAmount,
        deducted_from_invoices: totalDeductedFromInvoices,
        pending_amount: remainingFineAmount,
        updated_invoices: updatedInvoices,
        note: "Fine deducted from the same cancelled booking invoice only",
      },
    };
  } catch (error) {
    console.error("Error applying fine to app user:", error);
    throw error;
  }
}

/**
 * Get cancellation statistics
 */
export const getCancellationStatistics = async () => {
  try {
    const cancellationsRef = collection(db, "cancellation_track");
    const querySnapshot = await getDocs(cancellationsRef);

    const statistics = {
      total: 0,
      byProvider: {},
      byStatus: {},
      byType: {},
      totalFines: 0,
      pendingFines: 0,
      deductedFines: 0,
    };

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      statistics.total++;

      // Group by provider
      const providerId = data.service_provider_id || "unknown";
      if (!statistics.byProvider[providerId]) {
        statistics.byProvider[providerId] = {
          name: data.service_provider_name || "Unknown",
          count: 0,
          totalFines: 0,
        };
      }
      statistics.byProvider[providerId].count++;
      statistics.byProvider[providerId].totalFines +=
        data.total_fine || 0;

      // Group by status
      const status = data.status || "unknown";
      statistics.byStatus[status] = (statistics.byStatus[status] || 0) + 1;

      // Group by type
      const type = data.cancellation_type || "unknown";
      statistics.byType[type] = (statistics.byType[type] || 0) + 1;

      // Calculate fines
      const fine = data.total_fine || 0;
      statistics.totalFines += fine;

      if (data.deduction_status === "deducted") {
        statistics.deductedFines += fine;
      } else if (["pending", "pending_settlement", "partial"].includes(data.deduction_status)) {
        statistics.pendingFines += fine;
      }
    });

    return statistics;
  } catch (error) {
    console.error("Error fetching cancellation statistics:", error);
    throw error;
  }
};

/**
 * Get detailed provider information
 */
export const getProviderDetails = async (providerId) => {
  try {
    const providerRef = doc(db, "service_providers", providerId);
    const providerDoc = await getDoc(providerRef);

    if (providerDoc.exists()) {
      return {
        id: providerDoc.id,
        ...providerDoc.data(),
      };
    }

    return null;
  } catch (error) {
    console.error("Error fetching provider details:", error);
    throw error;
  }
};
